import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { lecturesApi } from '../../api/lectures'
import { coursesApi } from '../../api/courses'
import { subscriptionsApi } from '../../api/subscriptions'
import { progressApi } from '../../api/progress'
import { examsApi } from '../../api/exams'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import CustomVideoPlayer from '../../components/video/CustomVideoPlayer'
import BunnyVideoPlayer from '../../components/video/BunnyVideoPlayer'
import PDFViewer from '../../components/pdf/PDFViewer'
import Modal from '../../components/common/Modal'
import { 
  FiFile, FiDownload, FiBook, FiMessageCircle, FiFileText, FiLock, FiClipboard,
  FiChevronDown, FiChevronUp, FiCheckCircle, FiCircle, FiPlayCircle, FiLayers
} from 'react-icons/fi'
import Button from '../../components/common/Button'
import Card from '../../components/common/Card'
import './LecturePlayer.css'

const LecturePlayer = () => {
  const { courseId, lectureId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [activeTab, setActiveTab] = useState('video')
  const [selectedPDF, setSelectedPDF] = useState(null)
  const [showScreenshotWarning, setShowScreenshotWarning] = useState(false)
  const [showExamConfirmModal, setShowExamConfirmModal] = useState(false)
  const [selectedExam, setSelectedExam] = useState(null)
  const [expandedUnits, setExpandedUnits] = useState({})
  const [videoDuration, setVideoDuration] = useState(0)
  const [videoCurrentTime, setVideoCurrentTime] = useState(0)
  const [initialSeekTime, setInitialSeekTime] = useState(0)
  const [currentLectureProgress, setCurrentLectureProgress] = useState(null) // Store current lecture's saved progress
  const pageRef = useRef(null)
  const queryClient = useQueryClient()
  const progressSaveTimeoutRef = useRef(null)
  const lastSavedTimeRef = useRef(0)
  const videoPlayerRef = useRef(null)

  // Fetch lecture data
  const { data: lectureData, isLoading: lectureLoading, error: lectureError } = useQuery({
    queryKey: ['lecture', lectureId],
    queryFn: () => lecturesApi.getLecture(lectureId),
    enabled: !!lectureId,
  })

  // Fetch course progress
  const { data: progressData } = useQuery({
    queryKey: ['course-progress', courseId],
    queryFn: () => progressApi.getCourseProgress(courseId),
    enabled: !!courseId && isAuthenticated,
  })

  // Fetch course data to check subscription
  const { data: courseData } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => coursesApi.getCourse(courseId),
    enabled: !!courseId,
  })

  // Fetch user subscriptions to check access
  const { data: subscriptionsData } = useQuery({
    queryKey: ['user-subscriptions'],
    queryFn: () => subscriptionsApi.getUserSubscriptions(),
    enabled: isAuthenticated && !!user,
  })

  // Fetch course units for sidebar
  const { data: unitsData } = useQuery({
    queryKey: ['course-units', courseId],
    queryFn: () => coursesApi.getCourseUnits(courseId),
    enabled: !!courseId,
  })

  // Fetch lecture notes
  const { data: notesData } = useQuery({
    queryKey: ['lecture-notes', lectureId],
    queryFn: () => lecturesApi.getNotes({ lecture_id: lectureId }),
    enabled: !!lectureId && isAuthenticated,
    retry: false, // Don't retry on 404
    onError: (error) => {
      // Silently handle 404 errors for notes (notes might not exist yet)
      if (error?.response?.status !== 404) {
        console.error('Error fetching notes:', error)
      }
    },
  })

  // Fetch lecture exams
  const { data: examsData, isLoading: examsLoading } = useQuery({
    queryKey: ['lecture-exams', lectureId],
    queryFn: () => examsApi.getExams({ lecture_id: lectureId }),
    enabled: !!lectureId && isAuthenticated,
    retry: false,
  })

  // Mutation for saving progress
  const saveProgressMutation = useMutation({
    mutationFn: async (data) => {
      return await progressApi.saveProgress(data)
    },
    onSuccess: (response) => {
      // Store the saved progress for the current lecture
      if (response?.data && response.data.lecture_id?.toString() === lectureId) {
        setCurrentLectureProgress(response.data)
        // Also store in localStorage for persistence across reloads
        const storageKey = `lecture_progress_${lectureId}`
        localStorage.setItem(storageKey, JSON.stringify(response.data))
      }
      // Invalidate and refetch progress query to refresh the sidebar
      queryClient.invalidateQueries({ queryKey: ['course-progress', courseId] })
      queryClient.refetchQueries({ queryKey: ['course-progress', courseId] })
    },
    onError: (error) => {
      console.error('Failed to save progress:', error)
    },
  })

  // Debounced progress saving function
  const saveProgress = useCallback((progressPercentage, isCompleted = false) => {
    if (!isAuthenticated || !lectureId || !courseId || !lectureData?.data) return
    
    // Calculate watch time in seconds
    const watchTime = Math.floor(videoDuration * progressPercentage)
    
    // Only save if watch time has changed significantly (at least 5 seconds)
    if (Math.abs(watchTime - lastSavedTimeRef.current) < 5 && !isCompleted) {
      return
    }

    // Clear existing timeout
    if (progressSaveTimeoutRef.current) {
      clearTimeout(progressSaveTimeoutRef.current)
    }

    // Save immediately if completed, otherwise debounce
    if (isCompleted) {
      saveProgressMutation.mutate({
        course_id: parseInt(courseId),
        lecture_id: parseInt(lectureId),
        watch_time: watchTime,
        is_completed: true,
      })
      lastSavedTimeRef.current = watchTime
    } else {
      // Debounce: save after 5 seconds of no updates
      progressSaveTimeoutRef.current = setTimeout(() => {
        saveProgressMutation.mutate({
          course_id: parseInt(courseId),
          lecture_id: parseInt(lectureId),
          watch_time: watchTime,
          is_completed: false,
        })
        lastSavedTimeRef.current = watchTime
      }, 5000) // Save every 5 seconds
    }
  }, [isAuthenticated, lectureId, courseId, lectureData?.data, videoDuration, saveProgressMutation])

  // Handle video progress updates
  const handleVideoProgress = useCallback((progressPercentage, duration) => {
    // Use duration from callback if provided, otherwise fallback to state
    const effectiveDuration = duration || videoDuration
    if (!effectiveDuration || effectiveDuration <= 0) return
    
    const currentTime = effectiveDuration * progressPercentage
    setVideoCurrentTime(currentTime)
    
    // Update videoDuration if we got it from the callback and it's different
    if (duration && duration !== videoDuration) {
      setVideoDuration(duration)
    }
    
    // Save progress (debounced) - use effectiveDuration for calculation
    if (effectiveDuration > 0) {
      const watchTime = Math.floor(effectiveDuration * progressPercentage)
      // Only save if watch time has changed significantly (at least 5 seconds)
      if (Math.abs(watchTime - lastSavedTimeRef.current) >= 5) {
        // Clear existing timeout
        if (progressSaveTimeoutRef.current) {
          clearTimeout(progressSaveTimeoutRef.current)
        }
        
        // Debounce: save after 5 seconds of no updates
        progressSaveTimeoutRef.current = setTimeout(() => {
          if (isAuthenticated && lectureId && courseId && lectureData?.data) {
            saveProgressMutation.mutate({
              course_id: parseInt(courseId),
              lecture_id: parseInt(lectureId),
              watch_time: watchTime,
              is_completed: false,
            })
            lastSavedTimeRef.current = watchTime
          }
        }, 5000) // Save every 5 seconds
      }
    }
  }, [videoDuration, isAuthenticated, lectureId, courseId, lectureData?.data, saveProgressMutation])

  // Handle video ended
  const handleVideoEnded = useCallback(() => {
    // Mark as completed when video ends - use current videoDuration state
    if (isAuthenticated && lectureId && courseId && lectureData?.data && videoDuration > 0) {
      const watchTime = Math.floor(videoDuration)
      saveProgressMutation.mutate({
        course_id: parseInt(courseId),
        lecture_id: parseInt(lectureId),
        watch_time: watchTime,
        is_completed: true,
      })
      lastSavedTimeRef.current = watchTime
    }
  }, [isAuthenticated, lectureId, courseId, lectureData?.data, videoDuration, saveProgressMutation])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressSaveTimeoutRef.current) {
        clearTimeout(progressSaveTimeoutRef.current)
      }
    }
  }, [])

  // Reset video state when switching lectures
  useEffect(() => {
    // Reset video-related state when lectureId changes
    setVideoDuration(0)
    setVideoCurrentTime(0)
    setInitialSeekTime(0)
    lastSavedTimeRef.current = 0
    
    // Clear any pending progress saves
    if (progressSaveTimeoutRef.current) {
      clearTimeout(progressSaveTimeoutRef.current)
      progressSaveTimeoutRef.current = null
    }
  }, [lectureId])

  // Derived state
  const lecture = lectureData?.data
  const units = unitsData?.data?.units || []
  const notes = notesData?.data?.notes || []
  const course = lecture?.chapter?.unit?.course || courseData?.data

  // Get video URL (prefer signed_url, fallback to url) - memoized to recalculate when lecture changes
  const videoUrl = useMemo(() => {
    if (!lecture) return null
    const videoUrlRaw = lecture.video?.signed_url || lecture.video?.url || 
                        (lecture.videos && lecture.videos[0]?.signed_url) || 
                        (lecture.videos && lecture.videos[0]?.url)
    // Only process URL if it exists and is not empty
    return videoUrlRaw && videoUrlRaw.trim() ? getStorageUrl(videoUrlRaw) : null
  }, [lecture])

  // Check if video is from Bunny CDN (HLS stream) - memoized
  const isBunnyVideo = useMemo(() => {
    if (!videoUrl) return false
    const lowerUrl = videoUrl.toLowerCase()
    return lowerUrl.includes('mediadelivery.net') || 
           lowerUrl.includes('.m3u8') || 
           lowerUrl.includes('bunnycdn') ||
           lowerUrl.includes('b-cdn.net') ||
           lowerUrl.includes('bunny.net') ||
           lowerUrl.includes('bunny.stream')
  }, [videoUrl])

  // Helper to check completion status and calculate percentage
  const getLectureProgress = useCallback((lecId) => {
    // Check if this is the current lecture and we have saved progress
    if (lecId.toString() === lectureId) {
      // Prefer live playback time (updates every timeupdate), then fallback to saved progress
      const watchTime = Math.floor(
        (videoCurrentTime && videoCurrentTime > 0)
          ? videoCurrentTime
          : (currentLectureProgress?.watch_time || 0)
      )
      const isCompleted = currentLectureProgress?.is_completed || false
      
      // Find lecture duration
      let lectureDuration = 0
      for (const unit of units) {
        for (const chapter of unit.chapters || []) {
          for (const lec of chapter.lectures || []) {
            if (lec.id.toString() === lecId.toString()) {
              if (lec.duration) {
                lectureDuration = typeof lec.duration === 'number' ? lec.duration * 60 : parseInt(lec.duration) * 60
              }
              break
            }
          }
        }
      }

      // If backend didn't provide duration (common for Bunny/HLS), use the actual player duration if available
      if ((!lectureDuration || lectureDuration <= 0) && videoDuration && videoDuration > 0) {
        lectureDuration = Math.floor(videoDuration)
      }
      
      let percentage = 0
      if (isCompleted) {
        percentage = 100
      } else if (lectureDuration > 0 && watchTime > 0) {
        percentage = Math.min(100, Math.round((watchTime / lectureDuration) * 100))
      } else if (watchTime > 0) {
        // Still no duration: provide a smooth estimate so unit bars don't look stuck.
        // Keep it conservative (cap at 95% unless completed).
        const estimatedSeconds = 1800 // ~30 minutes per lecture
        percentage = Math.min(95, Math.max(1, Math.round((watchTime / estimatedSeconds) * 100)))
      }
      
      return {
        completed: isCompleted,
        percentage: percentage,
        watchTime: watchTime
      }
    }
    
    // For other lectures, try to get from localStorage
    const storageKey = `lecture_progress_${lecId}`
    const savedProgress = localStorage.getItem(storageKey)
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress)
        if (progress.lecture_id?.toString() === lecId.toString()) {
          const watchTime = progress.watch_time || 0
          const isCompleted = progress.is_completed || false
          
          // Find lecture duration
          let lectureDuration = 0
          for (const unit of units) {
            for (const chapter of unit.chapters || []) {
              for (const lec of chapter.lectures || []) {
                if (lec.id.toString() === lecId.toString()) {
                  if (lec.duration) {
                    lectureDuration = typeof lec.duration === 'number' ? lec.duration * 60 : parseInt(lec.duration) * 60
                  }
                  break
                }
              }
            }
          }
          
          let percentage = 0
          if (isCompleted) {
            percentage = 100
          } else if (lectureDuration > 0 && watchTime > 0) {
            percentage = Math.min(100, Math.round((watchTime / lectureDuration) * 100))
          }
          
          return {
            completed: isCompleted,
            percentage: percentage,
            watchTime: watchTime
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // No progress found
    return { completed: false, percentage: 0, watchTime: 0 }
  }, [lectureId, currentLectureProgress, units, videoCurrentTime, videoDuration])

  // Load saved progress from localStorage when lecture changes
  useEffect(() => {
    if (!lectureId) {
      setCurrentLectureProgress(null)
      return
    }
    
    // Try to load from localStorage
    const storageKey = `lecture_progress_${lectureId}`
    const savedProgress = localStorage.getItem(storageKey)
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress)
        if (progress.lecture_id?.toString() === lectureId) {
          setCurrentLectureProgress(progress)
        } else {
          setCurrentLectureProgress(null)
        }
      } catch (e) {
        console.error('Error parsing saved progress:', e)
        setCurrentLectureProgress(null)
      }
    } else {
      setCurrentLectureProgress(null)
    }
  }, [lectureId])

  // Load saved progress and resume video
  useEffect(() => {
    if (!lectureId || !videoDuration) return
    
    // Use currentLectureProgress if available, otherwise try to get from getLectureProgress
    let watchTime = 0
    let isCompleted = false
    
    if (currentLectureProgress && currentLectureProgress.lecture_id?.toString() === lectureId) {
      watchTime = currentLectureProgress.watch_time || 0
      isCompleted = currentLectureProgress.is_completed || false
    } else {
      const progress = getLectureProgress(parseInt(lectureId))
      watchTime = progress.watchTime
      isCompleted = progress.completed
    }
    
    if (watchTime > 0 && !isCompleted) {
      // Resume from saved position (but not if it's at the very end)
      const resumeTime = watchTime
      // Only resume if not too close to the end (within last 5 seconds)
      if (resumeTime < videoDuration - 5) {
        setInitialSeekTime(resumeTime)
      }
    }
  }, [lectureId, videoDuration, currentLectureProgress, getLectureProgress])

  // Find current lecture in course structure
  const findCurrentLecture = () => {
    for (const unit of units) {
      for (const chapter of unit.chapters || []) {
        for (const lec of chapter.lectures || []) {
          if (lec.id.toString() === lectureId) {
            return { unit, chapter, lecture: lec }
          }
        }
      }
    }
    return null
  }

  const currentLectureInfo = findCurrentLecture()
  const currentChapter = currentLectureInfo?.chapter
  const currentUnit = currentLectureInfo?.unit

  // Auto-expand current unit
  useEffect(() => {
    if (currentUnit?.id) {
      setExpandedUnits(prev => ({
        ...prev,
        [currentUnit.id]: true
      }))
    }
  }, [currentUnit?.id])

  // Check if user has access to this course.
  // Prefer backend `can_access` (strict package/course mapping).
  const canAccess = useMemo(() => {
    if (!isAuthenticated) return false

    if (typeof courseData?.data?.can_access === 'boolean') {
      return courseData.data.can_access
    }

    const course = courseData?.data
    if (!course || !subscriptionsData?.data?.subscriptions) return false

    const subscriptions = subscriptionsData.data.subscriptions
    const now = new Date()

    const activeSubs = subscriptions.filter((sub) => {
      if (sub.status !== 'active') return false
      if (!sub.expires_at) return false
      return new Date(sub.expires_at) > now
    })

    // Direct course subscription
    if (activeSubs.some((sub) => sub.course_id && sub.course_id.toString() === courseId)) return true

    // Package subscriptions: must include course OR subject
    const subjectId = course?.subject_id || course?.subject?.id
    for (const sub of activeSubs) {
      const pkg = sub.package
      if (!pkg) continue

      const pkgCourseIds = (pkg.courses || []).map((c) => c.id)
      if (pkgCourseIds.includes(Number(courseId))) return true

      const pkgSubjectIds = (pkg.subjects || []).map((s) => s.id)
      if (subjectId && pkgSubjectIds.includes(Number(subjectId))) return true
    }

    return false
  }, [isAuthenticated, subscriptionsData, courseId, courseData?.data])

  // Download and screenshot protection
  useEffect(() => {
    const container = pageRef.current
    if (!container) return

    // Disable right-click context menu
    const handleContextMenu = (e) => {
      // Allow context menu only for UI elements (buttons, links, etc.)
      if (e.target.closest('button') || 
          e.target.closest('a') || 
          e.target.closest('.lecture-tabs') ||
          e.target.closest('.pdf-viewer-header') ||
          e.target.closest('.pdf-viewer-controls')) {
        return true
      }
      e.preventDefault()
      return false
    }

    // Block keyboard shortcuts for downloading/saving
    const handleKeyDown = (e) => {
      // Block Ctrl+S (Save), Ctrl+P (Print), Ctrl+Shift+I (DevTools), F12, Print Screen
      if (
        (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'p' || e.key === 'P')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
        e.key === 'F12' ||
        e.keyCode === 44 || // Print Screen key code
        e.key === 'PrintScreen' || 
        e.key === 'Print' ||
        (e.key === 'F13') || // Some systems use F13 for Print Screen
        (e.ctrlKey && e.key === 'u') || // View source
        (e.ctrlKey && e.shiftKey && e.key === 'C') // DevTools
      ) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    }

    // Additional Print Screen detection
    const handleKeyUp = (e) => {
      if (e.keyCode === 44 || e.key === 'PrintScreen' || e.key === 'Print') {
        e.preventDefault()
        e.stopPropagation()
        // Show warning and blur content briefly
        if (container) {
          container.style.filter = 'blur(10px)'
          setShowScreenshotWarning(true)
          setTimeout(() => {
            if (container) container.style.filter = ''
            setShowScreenshotWarning(false)
          }, 2000)
        }
        return false
      }
    }

    // Detect Alt+PrintScreen (Windows) or Shift+Command+3/4 (Mac)
    const handleKeyPress = (e) => {
      if (
        (e.altKey && e.keyCode === 44) ||
        (e.shiftKey && e.metaKey && (e.keyCode === 51 || e.keyCode === 52))
      ) {
        e.preventDefault()
        e.stopPropagation()
        if (container) {
          container.style.filter = 'blur(10px)'
          setShowScreenshotWarning(true)
          setTimeout(() => {
            if (container) container.style.filter = ''
            setShowScreenshotWarning(false)
          }, 2000)
        }
        return false
      }
    }

    // Disable text selection on video/PDF content
    const handleSelectStart = (e) => {
      // Allow selection only for UI elements
      if (e.target.closest('button') || 
          e.target.closest('a') || 
          e.target.closest('input') ||
          e.target.closest('textarea') ||
          e.target.closest('.lecture-tabs') ||
          e.target.closest('.video-info-section') ||
          e.target.closest('.note-content')) {
        return true
      }
      // Block selection on video players and PDF content
      if (e.target.closest('video') || 
          e.target.closest('.react-pdf__Page') ||
          e.target.closest('.video-player-wrapper') ||
          e.target.closest('.pdf-viewer-content')) {
        e.preventDefault()
        return false
      }
    }

    // Block drag and drop
    const handleDragStart = (e) => {
      // Allow drag only for UI elements
      if (e.target.closest('button') || e.target.closest('a')) {
        return true
      }
      // Block drag on video/PDF content
      if (e.target.closest('video') || 
          e.target.closest('.react-pdf__Page') ||
          e.target.closest('.video-player-wrapper') ||
          e.target.closest('.pdf-viewer-content')) {
        e.preventDefault()
        return false
      }
    }

    // Block video element right-click and download
    const handleVideoContextMenu = (e) => {
      e.preventDefault()
      return false
    }

    // Disable video download attribute
    const disableVideoDownloads = () => {
      const videos = container.querySelectorAll('video')
      videos.forEach(video => {
        video.setAttribute('controlsList', 'nodownload')
        video.setAttribute('disablePictureInPicture', 'true')
        video.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback')
        // Remove download attribute if present
        video.removeAttribute('download')
      })
    }

    // Disable print
    const handleBeforePrint = () => {
      if (container) {
        container.style.filter = 'blur(20px)'
        setShowScreenshotWarning(true)
      }
    }

    const handleAfterPrint = () => {
      if (container) {
        container.style.filter = ''
        setShowScreenshotWarning(false)
      }
    }

    // Detect print media query
    const printMediaQuery = window.matchMedia('print')
    const handlePrintChange = (mq) => {
      if (mq.matches) {
        handleBeforePrint()
      } else {
        handleAfterPrint()
      }
    }

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu, true)
    document.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('keyup', handleKeyUp, true)
    document.addEventListener('keypress', handleKeyPress, true)
    document.addEventListener('selectstart', handleSelectStart, true)
    document.addEventListener('dragstart', handleDragStart, true)
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)
    printMediaQuery.addEventListener('change', handlePrintChange)

    // Disable video downloads
    disableVideoDownloads()
    
    // Watch for new video elements
    const observer = new MutationObserver(() => {
      disableVideoDownloads()
    })
    
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true
      })
    }

    // Block video right-click
    const videos = container.querySelectorAll('video')
    videos.forEach(video => {
      video.addEventListener('contextmenu', handleVideoContextMenu, true)
    })

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('keyup', handleKeyUp, true)
      document.removeEventListener('keypress', handleKeyPress, true)
      document.removeEventListener('selectstart', handleSelectStart, true)
      document.removeEventListener('dragstart', handleDragStart, true)
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      printMediaQuery.removeEventListener('change', handlePrintChange)
      observer.disconnect()
      videos.forEach(video => {
        video.removeEventListener('contextmenu', handleVideoContextMenu, true)
      })
    }
  }, [])

  if (lectureLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  if (lectureError) {
    const errorCode = lectureError.response?.data?.error_code
    const isRevisionError = errorCode === 'REVISION_ACCESS_REQUIRED' || errorCode === 'REVISION_ONLY_PACKAGE'
    const errorMessage = isRevisionError 
      ? (errorCode === 'REVISION_ONLY_PACKAGE' ? 'اشتراكك يشمل محاضرات المراجعة فقط' : 'هذه المحاضرة تتطلب اشتراك المراجعة')
      : (lectureError.response?.data?.message || 'حدث خطأ أثناء تحميل المحاضرة')
    return (
      <>
        <Header />
        <div className="error-container">
          {isRevisionError && <FiLock size={48} style={{ marginBottom: '16px', opacity: 0.6 }} />}
          <p>{errorMessage}</p>
          {lectureError.response?.status === 403 && (
            <Link to={isRevisionError ? '/subscriptions' : `/courses/${courseId}`}>
              <button className="back-button">{isRevisionError ? 'عرض الاشتراكات' : 'العودة إلى الدورة'}</button>
            </Link>
          )}
        </div>
        <Footer />
      </>
    )
  }

  if (!lecture) {
    return (
      <>
        <Header />
        <div className="error-container">
          <p>المحاضرة غير موجودة</p>
        </div>
        <Footer />
      </>
    )
  }

  // Check access - block if not allowed
  if (!canAccess) {
    return (
      <>
        <Header />
        <div className="lecture-access-denied">
          <div className="access-denied-content">
            <FiLock className="lock-icon" />
            <h2>الوصول محظور</h2>
            <p>
              {isAuthenticated 
                ? 'يجب الاشتراك في هذه الدورة للوصول إلى المحاضرات'
                : 'يجب تسجيل الدخول والاشتراك في هذه الدورة للوصول إلى المحاضرات'}
            </p>
            <div className="access-denied-actions">
              {isAuthenticated ? (
                <>
                  <Link to={`/courses/${courseId}`}>
                    <Button variant="primary">
                      عرض تفاصيل الدورة
                    </Button>
                  </Link>
                  <Link to={`/subscriptions`}>
                    <Button variant="primary" style={{ marginTop: '12px' }}>
                      اشترك الآن
                    </Button>
                  </Link>
                </>
              ) : (
                <Link to="/login">
                  <Button variant="primary">
                    تسجيل الدخول
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  // Get video processing status from lecture data
  // Trust the backend's 'processing' field as the authoritative source
  const getVideoProcessingStatus = () => {
    const video = lecture?.video || (lecture?.videos && lecture?.videos[0])
    if (!video) return { isProcessing: false, reason: '' }
    
    // Only check the processing field from backend - it knows if video is ready
    const isProcessing = video.processing === true
    const reason = video.processing_reason || ''
    
    return { isProcessing, reason }
  }

  const videoStatus = getVideoProcessingStatus()

  const toggleUnit = (unitId) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }))
  }

  // Calculate unit progress
  const getUnitProgress = (unit) => {
    if (!unit.chapters) return 0
    let totalLectures = 0
    let sumPercentages = 0
    
    unit.chapters.forEach(chapter => {
      if (chapter.lectures) {
        chapter.lectures.forEach(lec => {
          totalLectures++
          sumPercentages += (getLectureProgress(lec.id).percentage || 0)
        })
      }
    })
    
    return totalLectures > 0 ? Math.round(sumPercentages / totalLectures) : 0
  }

  return (
    <div className="lecture-player-page bg-gray-900 min-h-screen" ref={pageRef}>
      <Header />
      {showScreenshotWarning && (
        <div className="screenshot-warning-overlay">
          <div className="screenshot-warning-content">
            <h3>⚠️ Screenshots are not allowed</h3>
            <p>This content is protected</p>
          </div>
        </div>
      )}
      <main className="lecture-player-main bg-gray-900">
        {/* Top Navigation Bar */}
        <div className="lecture-top-nav">
          <div className="user-email-badge">
            {user?.email || '3afer'}
          </div>
          <div className="course-breadcrumb">
            {course && (
              <>
                <Link to={`/courses/${course.id}`} className="breadcrumb-link">
                  {course.title}
                </Link>
                {currentChapter && (
                  <>
                    <span className="breadcrumb-separator">←</span>
                    <span className="breadcrumb-current">{currentChapter.title}</span>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="lecture-container">
          {/* Video Section */}
          <div className="lecture-video-section">
            <div className="video-player-wrapper">
              {videoUrl ? (
                isBunnyVideo ? (
                  <BunnyVideoPlayer
                    key={`bunny-${lectureId}-${videoUrl}`}
                    url={videoUrl}
                    watermark={user?.email || '3afer'}
                    poster={lecture.thumbnail && lecture.thumbnail.trim() ? getStorageUrl(lecture.thumbnail) : null}
                    isProcessing={videoStatus.isProcessing}
                    processingReason={videoStatus.reason}
                    initialTime={initialSeekTime}
                    onProgress={(progress, duration) => {
                      if (duration && duration > 0) {
                        setVideoDuration(duration)
                      }
                      handleVideoProgress(progress, duration)
                    }}
                    onEnded={handleVideoEnded}
                  />
                ) : (
                  <CustomVideoPlayer
                    key={`custom-${lectureId}-${videoUrl}`}
                    url={videoUrl}
                    watermark={user?.email || '3afer'}
                    initialTime={initialSeekTime}
                    onProgress={(progress, duration) => {
                      if (duration && duration > 0) {
                        setVideoDuration(duration)
                      }
                      handleVideoProgress(progress, duration)
                    }}
                    onEnded={handleVideoEnded}
                  />
                )
              ) : (
                <div className="no-video">
                  <p>لا يوجد فيديو متاح لهذه المحاضرة</p>
                </div>
              )}
            </div>

            {/* Tabs - Below Video */}
            <div className="lecture-tabs">
              <button
                className={activeTab === 'video' ? 'tab active' : 'tab'}
                onClick={() => setActiveTab('video')}
              >
                الفيديو
              </button>
              {isAuthenticated && (
                <button
                  className={activeTab === 'notes' ? 'tab active' : 'tab'}
                  onClick={() => setActiveTab('notes')}
                >
                  الملاحظات {notes.length > 0 && <span className="tab-badge">{notes.length}</span>}
                </button>
              )}
              {isAuthenticated && (
                <button
                  className={activeTab === 'exams' ? 'tab active' : 'tab'}
                  onClick={() => setActiveTab('exams')}
                >
                  الامتحانات
                </button>
              )}
              {lecture.materials && lecture.materials.length > 0 && (
                <button
                  className={activeTab === 'materials' ? 'tab active' : 'tab'}
                  onClick={() => setActiveTab('materials')}
                >
                  المواد <span className="tab-badge">{lecture.materials.length}</span>
                </button>
              )}
            </div>

            {/* Tab Content */}
            <div className="lecture-tab-content">
              {activeTab === 'video' && (
                <div className="video-info-section">
                  <h2 className="lecture-title">{lecture.title}</h2>
                  {lecture.description && (
                    <p className="lecture-description">{lecture.description}</p>
                  )}
                </div>
              )}

              {activeTab === 'materials' && (
                <div className="materials-list">
                  {lecture.materials && lecture.materials.length > 0 ? (
                    lecture.materials.map((material) => {
                      const isPDF = material.name?.toLowerCase().endsWith('.pdf') || 
                                   material.url?.toLowerCase().includes('.pdf') ||
                                   material.type === 'application/pdf'
                      // Prefer signed_url for PDFs to avoid CORS issues
                      const materialUrlRaw = material.signed_url || material.url
                      const materialUrl = materialUrlRaw && materialUrlRaw.trim() ? (
                        materialUrlRaw.startsWith('http://') || materialUrlRaw.startsWith('https://')
                          ? materialUrlRaw
                          : getStorageUrl(materialUrlRaw)
                      ) : null
                      
                      return (
                        <div
                          key={material.id}
                          className="material-item"
                        >
                          {isPDF ? (
                            <FiFileText className="material-icon pdf-icon" />
                          ) : (
                            <FiFile className="material-icon" />
                          )}
                          <div className="material-info">
                            <span className="material-name">{material.name || 'ملف'}</span>
                            {material.size && (
                              <span className="material-size">{(material.size / 1024 / 1024).toFixed(2)} MB</span>
                            )}
                          </div>
                          <div className="material-actions">
                            {isPDF ? (
                              <button
                                className="material-action-btn"
                                onClick={() => setSelectedPDF({ 
                                  url: materialUrl, 
                                  name: material.name,
                                  signedUrl: material.signed_url 
                                })}
                                title="عرض PDF"
                              >
                                <FiFileText />
                              </button>
                            ) : null}
                            {/* Download button removed - content is protected */}
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <p className="empty-state">لا توجد مواد متاحة</p>
                  )}
                </div>
              )}

              {activeTab === 'notes' && isAuthenticated && (
                <div className="notes-section">
                  {notes.length > 0 ? (
                    <div className="notes-list">
                      {notes.map((note) => (
                        <div key={note.id} className="note-item">
                          <p className="note-content">{note.content}</p>
                          <span className="note-date">
                            {new Date(note.created_at).toLocaleDateString('ar-EG')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-state">لا توجد ملاحظات</p>
                  )}
                </div>
              )}

              {activeTab === 'exams' && isAuthenticated && (
                <div className="exams-section">
                  {examsLoading ? (
                    <div className="loading-state">
                      <Loading text="جاري تحميل الامتحانات..." />
                    </div>
                  ) : examsData?.data?.exams && examsData.data.exams.length > 0 ? (
                    <div className="exams-list">
                      {examsData.data.exams.map((exam) => (
                        <Card key={exam.id} className="exam-card">
                          <div 
                            className="exam-card-content" 
                            onClick={() => {
                              setSelectedExam(exam)
                              setShowExamConfirmModal(true)
                            }}
                          >
                            <div className="exam-icon-wrapper">
                              <FiClipboard className="exam-icon" />
                            </div>
                            <div className="exam-info">
                              <h3 className="exam-title">{exam.title}</h3>
                              <div className="exam-meta">
                                <span className="exam-duration">
                                  {exam.duration_minutes || exam.duration} دقيقة
                                </span>
                                {exam.passing_score && (
                                  <span className="exam-passing-score">
                                    الدرجة المطلوبة: {exam.passing_score}%
                                  </span>
                                )}
                              </div>
                              {exam.description && (
                                <p className="exam-description">{exam.description}</p>
                              )}
                            </div>
                            <div className="exam-arrow">
                              <FiFileText />
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <FiClipboard className="empty-icon" />
                      <p>لا توجد امتحانات متاحة لهذه المحاضرة</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lecture-sidebar">
            <div className="sidebar-header">
              <h3 className="sidebar-title">محتوى الدورة</h3>
            </div>
            <div className="course-structure">
              <div className="timeline-line"></div>
              {units.map((unit, index) => {
                const isExpanded = expandedUnits[unit.id]
                const progress = getUnitProgress(unit)
                
                return (
                  <div key={unit.id} className="timeline-item">
                    <div className="timeline-marker">
                      {index + 1}
                    </div>
                    <div className={`unit-section ${isExpanded ? 'expanded' : ''}`}>
                      <div 
                        className="unit-header"
                        onClick={() => toggleUnit(unit.id)}
                      >
                        <div className="unit-info">
                          <div className="unit-title-wrapper">
                            <h4 className="unit-title">{unit.title}</h4>
                            <FiLayers className="unit-type-icon" />
                          </div>
                          
                          <div className="unit-meta">
                            <span className="unit-progress-text">{progress}%</span>
                            <div className="unit-progress-bar">
                              <div 
                                className="unit-progress-fill" 
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="unit-toggle-icon">
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="unit-content">
                          {unit.chapters && unit.chapters.map((chapter) => (
                            <div key={chapter.id} className="chapter-section">
                              {chapter.title && <h5 className="chapter-title">{chapter.title}</h5>}
                              <div className="lectures-list">
                                {chapter.lectures && chapter.lectures.map((lec) => {
                                  const isActive = lec.id.toString() === lectureId
                                  const { completed } = getLectureProgress(lec.id)
                                  const isLocked = !canAccess && !lec.is_free 
                                  
                                  return (
                                    <button
                                      key={lec.id}
                                      className={`lecture-item ${isActive ? 'active' : ''} ${completed ? 'completed' : ''}`}
                                      onClick={() => navigate(`/courses/${courseId}/lectures/${lec.id}`)}
                                      disabled={!canAccess && !lec.is_free && !isActive}
                                    >
                                      <div className="lecture-status-wrapper">
                                        {completed ? (
                                          <FiCheckCircle className="status-icon completed" />
                                        ) : isActive ? (
                                          <FiPlayCircle className="status-icon active" />
                                        ) : (
                                          <FiCircle className="status-icon pending" />
                                        )}
                                      </div>
                                      <div className="lecture-content-wrapper">
                                        <span className="lecture-name">{lec.title}</span>
                                        <div className="lecture-meta">
                                          {lec.duration && (
                                            <span className="lecture-duration">
                                              {lec.duration} دقيقة
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      
      {selectedPDF && (
        <PDFViewer
          url={selectedPDF.url}
          fileName={selectedPDF.name}
          signedUrl={selectedPDF.signedUrl}
          onClose={() => setSelectedPDF(null)}
        />
      )}

      {/* Exam Confirmation Modal */}
      {showExamConfirmModal && selectedExam && (
        <Modal
          isOpen={showExamConfirmModal}
          onClose={() => {
            setShowExamConfirmModal(false)
            setSelectedExam(null)
          }}
          title=""
          size="sm"
        >
          <div className="exam-confirm-modal-content">
            <div className="exam-confirm-icon-wrapper">
              <FiClipboard className="exam-confirm-icon" />
            </div>
            <h2 className="exam-confirm-title">بدء الامتحان</h2>
            <p className="exam-confirm-message">
              هل أنت متأكد أنك تريد بدء الامتحان الآن؟ سيتم حساب الوقت بمجرد البدء.
            </p>
            {selectedExam && (
              <div className="exam-confirm-details">
                <div className="exam-confirm-detail-item">
                  <span className="exam-confirm-label">اسم الامتحان</span>
                  <span className="exam-confirm-value">{selectedExam.title}</span>
                </div>
                {selectedExam.duration_minutes || selectedExam.duration ? (
                  <div className="exam-confirm-detail-item">
                    <span className="exam-confirm-label">المدة</span>
                    <span className="exam-confirm-value">{selectedExam.duration_minutes || selectedExam.duration} دقيقة</span>
                  </div>
                ) : null}
                {selectedExam.passing_score ? (
                  <div className="exam-confirm-detail-item">
                    <span className="exam-confirm-label">الدرجة المطلوبة</span>
                    <span className="exam-confirm-value">{selectedExam.passing_score}%</span>
                  </div>
                ) : null}
              </div>
            )}
            <div className="exam-confirm-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowExamConfirmModal(false)
                  setSelectedExam(null)
                }}
                fullWidth
              >
                إلغاء
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowExamConfirmModal(false)
                  navigate(`/exams/${selectedExam.id}/take`)
                  setSelectedExam(null)
                }}
                fullWidth
              >
                ابدأ الامتحان
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default LecturePlayer

